import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useProjectSiteMonitor, useRunMonitorChecks } from '@/hooks/use-site-monitors';
import { toast } from '@/hooks/use-toast';
import { AddSiteMonitorDialog } from '@/components/site-monitoring/add-monitor-dialog';
import { cn } from '@/lib/utils';
import {
  Activity, Shield, Globe, Gauge, Search, Lock, Server, Plus,
  AlertTriangle, CheckCircle, XCircle, Clock, ExternalLink, TrendingUp, TrendingDown, Minus,
  RefreshCw, Loader2,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface SiteHealthTabProps {
  projectId: string;
  isAdmin?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────

const statusMeta: Record<string, { label: string; color: string; icon: typeof CheckCircle }> = {
  pass: { label: 'Pass', color: 'text-dyndev-green bg-dyndev-green/10', icon: CheckCircle },
  warn: { label: 'Warning', color: 'text-yellow-400 bg-yellow-500/10', icon: AlertTriangle },
  fail: { label: 'Fail', color: 'text-red-400 bg-red-500/10', icon: XCircle },
};

function StatusBadge({ status }: { status: string }) {
  const meta = statusMeta[status] || statusMeta.pass;
  const Icon = meta.icon;
  return (
    <span className={cn('inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full', meta.color)}>
      <Icon size={10} />{meta.label}
    </span>
  );
}

function MetricCard({ icon: Icon, iconColor, title, tooltip, children, className }: {
  icon: any; iconColor: string; title: string; tooltip?: string; children: React.ReactNode; className?: string;
}) {
  const titleEl = (
    <div className="flex items-center gap-2">
      <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center', 'bg-[var(--bg-secondary)]')}>
        <Icon size={14} className={iconColor} />
      </div>
      <CardTitle className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{title}</CardTitle>
    </div>
  );

  return (
    <Card className={cn('hover:border-dyndev-green/10 transition-colors', className)}>
      <CardHeader className="pb-2">
        {tooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>{titleEl}</TooltipTrigger>
            <TooltipContent side="top" className="max-w-[260px] text-xs leading-relaxed">{tooltip}</TooltipContent>
          </Tooltip>
        ) : (
          titleEl
        )}
      </CardHeader>
      <CardContent className="pt-0">{children}</CardContent>
    </Card>
  );
}

function TitleWithTooltip({ icon: Icon, iconColor, title, tooltip, side = 'top' }: {
  icon: any; iconColor: string; title: string; tooltip: string; side?: 'top' | 'right' | 'bottom' | 'left';
}) {
  const el = (
    <div className="flex items-center gap-2">
      <Icon size={14} className={iconColor} />
      <CardTitle className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{title}</CardTitle>
    </div>
  );
  return (
    <Tooltip>
      <TooltipTrigger asChild>{el}</TooltipTrigger>
      <TooltipContent side={side} sideOffset={side === 'right' ? 8 : 4} className="max-w-[260px] text-xs leading-relaxed">{tooltip}</TooltipContent>
    </Tooltip>
  );
}

const daysRemainingMeta = (days: number) => {
  if (days <= 14) return { color: 'text-red-400', bg: 'bg-red-500/10' };
  if (days <= 30) return { color: 'text-yellow-400', bg: 'bg-yellow-500/10' };
  return { color: 'text-dyndev-green', bg: 'bg-dyndev-green/10' };
};

const formatCheckTime = (iso?: string) => {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' ' +
    d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

const WEB_VITAL_METRICS = [
  { label: 'FCP', desc: 'First Contentful Paint', tooltip: 'Time until the first text or image is painted. Good: ≤ 1.8s · Needs work: 1.8–3.0s · Poor: > 3.0s' },
  { label: 'LCP', desc: 'Largest Contentful Paint', tooltip: 'Time until the largest content element is rendered. Good: ≤ 2.5s · Needs work: 2.5–4.0s · Poor: > 4.0s' },
  { label: 'TBT', desc: 'Total Blocking Time', tooltip: 'Total time the main thread was blocked. Good: ≤ 200ms · Needs work: 200–600ms · Poor: > 600ms' },
  { label: 'CLS', desc: 'Cumulative Layout Shift', tooltip: 'Measures visual stability — how much elements shift during load. Good: ≤ 0.1 · Needs work: 0.1–0.25 · Poor: > 0.25' },
];

// ─── Main Component ──────────────────────────────────────────────

export function SiteHealthTab({ projectId, isAdmin }: SiteHealthTabProps) {
  const { data, isLoading, isError } = useProjectSiteMonitor(projectId);
  const runChecks = useRunMonitorChecks();
  const monitor = data?.data;
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const handleRunChecks = async () => {
    try {
      const result = await runChecks.mutateAsync(projectId);
      toast({
        title: 'Checks complete',
        description: `${result.data?.checked || 0} checks ran, ${result.data?.skipped || 0} skipped, ${result.data?.errors || 0} errors.`,
        variant: 'success',
      });
    } catch (err: any) {
      toast({
        title: 'Failed to run checks',
        description: err?.message || 'An error occurred.',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4 space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-3 w-24" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (isError || !monitor) {
    return (
      <>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Activity size={40} className="text-[var(--text-secondary)] mb-4" />
          <h3 className="text-lg font-semibold mb-1">No Site Monitoring</h3>
          <p className="text-sm text-[var(--text-secondary)] max-w-sm mb-4">
            {isAdmin
              ? 'Configure health monitoring for this project\'s website. Checks include uptime, SSL, domain expiry, PageSpeed, security headers, and more.'
              : 'Site health monitoring is not currently configured for this project.'}
          </p>
          {isAdmin && (
            <Button size="sm" onClick={() => setAddDialogOpen(true)} className="gap-1.5">
              <Plus size={14} />
              Configure Monitoring
            </Button>
          )}
        </div>
        <AddSiteMonitorDialog
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
          defaultProjectId={projectId}
        />
      </>
    );
  }

  // Get latest check for each type
  const latestChecks: Record<string, any> = {};
  for (const c of monitor.checks || []) {
    if (!latestChecks[c.checkType]) latestChecks[c.checkType] = c;
  }

  const uptime = latestChecks.uptime;
  const ssl = latestChecks.ssl;
  const domain = latestChecks.domain;
  const pagespeed = latestChecks.pagespeed;
  const security = latestChecks.security;
  const seo = latestChecks.seo;
  const cms = latestChecks.wordpress;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Activity size={16} className="text-dyndev-green" />
            {monitor.monitoredUrl}
          </h3>
          <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">
            Last checked: {monitor.lastCheckedAt ? new Date(monitor.lastCheckedAt).toLocaleString() : 'Never'}
            {!monitor.isActive && ' · (Paused)'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleRunChecks}
              disabled={runChecks.isPending}
              className="gap-1.5 text-xs"
            >
              {runChecks.isPending ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <RefreshCw size={12} />
              )}
              {runChecks.isPending ? 'Running...' : 'Run Checks Now'}
            </Button>
          )}
          <a href={monitor.monitoredUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-dyndev-green hover:underline flex items-center gap-1">
            Visit Site <ExternalLink size={10} />
          </a>
        </div>
      </div>

      {/* Top metric cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Uptime */}
        <MetricCard icon={Activity} iconColor="text-blue-400" title="Uptime" tooltip="Whether the site responds to HTTP requests via a direct HTTP ping. The percentage shown is a rolling 30-day average of pass/fail checks.">
          {uptime ? (
            <>
              {uptime.details?.error ? (
                <div className="mt-1">
                  <span className="text-sm text-red-400">
                    {uptime.details.error.includes('ECONNREFUSED') || uptime.details.error.includes('ENOTFOUND')
                      ? 'Site unreachable'
                      : uptime.details.error.includes('timeout') || uptime.details.error.includes('ETIMEDOUT')
                        ? 'Request timed out'
                        : 'Check failed'}
                  </span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="text-[10px] text-[var(--text-secondary)] mt-1 truncate cursor-help">{uptime.details.error}</p>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[320px] text-xs leading-relaxed break-all">{uptime.details.error}</TooltipContent>
                  </Tooltip>
                </div>
              ) : uptime.details?.skipped ? (
                <div className="mt-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm text-[var(--text-secondary)]">Not configured</span>
                    <StatusBadge status={uptime.status} />
                  </div>
                  <p className="text-[10px] text-[var(--text-secondary)] mt-1">{uptime.details.reason}</p>
                </div>
              ) : (
                <>
                  <div className="flex items-baseline gap-1.5 mt-1">
                    <span className="text-2xl font-bold">{uptime.score ?? '—'}%</span>
                    <StatusBadge status={uptime.status} />
                  </div>
                  <p className="text-[10px] text-[var(--text-secondary)] mt-1">
                    {uptime.details?.provider === 'http-ping' && !uptime.details?.rollingUptimePercent
                      ? 'Live ping'
                      : '30-day average'}
                  </p>
                  {uptime.details?.responseTimeMs && (
                    <p className="text-[10px] text-[var(--text-secondary)]">{uptime.details.responseTimeMs}ms response</p>
                  )}
                </>
              )}
              {uptime.checkedAt && (
                <p className="text-[9px] text-[var(--text-secondary)] mt-2">Checked: {formatCheckTime(uptime.checkedAt)}</p>
              )}
            </>
          ) : (
            <p className="text-sm text-[var(--text-secondary)] mt-1">Pending first check</p>
          )}
        </MetricCard>

        {/* SSL */}
        <MetricCard icon={Lock} iconColor="text-purple-400" title="SSL" tooltip="Monitors the TLS/SSL certificate expiry. Red: expires within 14 days — urgent. Yellow: within 30 days — renew soon. Also shows the certificate issuer.">
          {ssl ? (
            <>
              {ssl.details?.error ? (
                <div className="mt-1">
                  <span className="text-sm text-red-400">
                    {ssl.details.error.includes('ECONNREFUSED') || ssl.details.error.includes('ENOTFOUND')
                      ? 'No SSL / unreachable'
                      : ssl.details.error.includes('expired') || ssl.details.error.includes('certificate')
                        ? 'Certificate issue'
                        : 'Check failed'}
                  </span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="text-[10px] text-[var(--text-secondary)] mt-1 truncate cursor-help">{ssl.details.error}</p>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[320px] text-xs leading-relaxed break-all">{ssl.details.error}</TooltipContent>
                  </Tooltip>
                </div>
              ) : (
                <>
                  <div className="flex items-baseline gap-1.5 mt-1">
                    <span className={cn('text-2xl font-bold', daysRemainingMeta(ssl.details?.daysRemaining || 0).color)}>
                      {ssl.details?.daysRemaining ?? '—'}
                    </span>
                    <span className="text-xs text-[var(--text-secondary)]">days remaining</span>
                    <StatusBadge status={ssl.status} />
                  </div>
                  <p className="text-[10px] text-[var(--text-secondary)] mt-1 truncate">{ssl.details?.issuer || ''}</p>
                </>
              )}
              {ssl.checkedAt && (
                <p className="text-[9px] text-[var(--text-secondary)] mt-2">Checked: {formatCheckTime(ssl.checkedAt)}</p>
              )}
            </>
          ) : (
            <p className="text-sm text-[var(--text-secondary)] mt-1">Pending first check</p>
          )}
        </MetricCard>

        {/* Domain */}
        <MetricCard icon={Globe} iconColor="text-orange-400" title="Domain" tooltip="WHOIS lookup of the domain registration expiry. Separate from SSL — this is when the domain name itself lapses. Red: within 30 days — urgent.">
          {domain ? (
            <>
              {domain.details?.error ? (
                <div className="mt-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm text-[var(--text-secondary)]">
                      {domain.details.error.includes('not found') || domain.details.error.includes('NXDOMAIN') || domain.details.error.includes('No match')
                        ? 'Not registered'
                        : domain.details.error.includes('timeout') || domain.details.error.includes('ETIMEDOUT')
                          ? 'Lookup timeout'
                          : 'Unavailable'}
                    </span>
                    <StatusBadge status={domain.status} />
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="text-[10px] text-[var(--text-secondary)] mt-1 truncate cursor-help">
                        {domain.details.error.includes('not found') || domain.details.error.includes('NXDOMAIN') || domain.details.error.includes('No match')
                          ? 'Domain is not registered'
                          : domain.details.error.includes('timeout') || domain.details.error.includes('ETIMEDOUT')
                            ? 'WHOIS server did not respond in time'
                            : 'WHOIS lookup failed'}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[320px] text-xs leading-relaxed break-all">{domain.details.error}</TooltipContent>
                  </Tooltip>
                </div>
              ) : (
                <>
                  <div className="flex items-baseline gap-1.5 mt-1">
                    <span className={cn('text-2xl font-bold', daysRemainingMeta(domain.details?.daysRemaining || 0).color)}>
                      {domain.details?.daysRemaining ?? '—'}
                    </span>
                    <span className="text-xs text-[var(--text-secondary)]">days remaining</span>
                    <StatusBadge status={domain.status} />
                  </div>
                  <p className="text-[10px] text-[var(--text-secondary)] mt-1 truncate">{domain.details?.registrar || ''}</p>
                </>
              )}
              {domain.checkedAt && (
                <p className="text-[9px] text-[var(--text-secondary)] mt-2">Checked: {formatCheckTime(domain.checkedAt)}</p>
              )}
            </>
          ) : (
            <p className="text-sm text-[var(--text-secondary)] mt-1">Pending first check</p>
          )}
        </MetricCard>

        {/* PageSpeed — simplified top card without inline scores */}
        <MetricCard icon={Gauge} iconColor="text-emerald-400" title="PageSpeed" tooltip="Google PageSpeed Insights score (0–100), averaged from mobile and desktop Lighthouse audits. 90+ excellent · 50–89 needs work · below 50 poor. Includes Core Web Vitals.">
          {pagespeed ? (
            <>
              {pagespeed.details?.error ? (
                <div className="mt-1">
                  <span className="text-sm text-red-400">
                    {pagespeed.details.error.includes('API') || pagespeed.details.error.includes('key')
                      ? 'API key missing'
                      : pagespeed.details.error.includes('timeout') || pagespeed.details.error.includes('ETIMEDOUT')
                        ? 'Request timed out'
                        : 'Check failed'}
                  </span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="text-[10px] text-[var(--text-secondary)] mt-1 truncate cursor-help">{pagespeed.details.error}</p>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[320px] text-xs leading-relaxed break-all">{pagespeed.details.error}</TooltipContent>
                  </Tooltip>
                </div>
              ) : pagespeed.details?.skipped ? (
                <div className="mt-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm text-[var(--text-secondary)]">Not configured</span>
                    <StatusBadge status={pagespeed.status} />
                  </div>
                  <p className="text-[10px] text-[var(--text-secondary)] mt-1">{pagespeed.details.reason}</p>
                </div>
              ) : (
                <>
                  <div className="flex items-baseline gap-1.5 mt-1">
                    <span className="text-2xl font-bold">{pagespeed.score ?? '—'}</span>
                    <span className="text-[10px] text-[var(--text-secondary)]">/100</span>
                    <StatusBadge status={pagespeed.status} />
                  </div>
                  {/* Trend with tooltips */}
                  {pagespeed.details?.previousScores?.length > 1 && (
                    <div className="flex items-center gap-0.5 mt-2">
                      {pagespeed.details.previousScores.slice(-6).map((s: number, i: number) => {
                        const prev = pagespeed.details.previousScores[i - 1];
                        const trend = prev ? (s > prev ? 'up' : s < prev ? 'down' : 'flat') : 'flat';
                        const tooltip = prev
                          ? `Score trend: ${prev} → ${s} (${trend === 'up' ? 'improved' : trend === 'down' ? 'declined' : 'unchanged'})`
                          : `Score: ${s}`;
                        return (
                          <Tooltip key={i}>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-0.5 cursor-help">
                                {trend === 'up' ? <TrendingUp size={8} className="text-dyndev-green" /> :
                                 trend === 'down' ? <TrendingDown size={8} className="text-red-400" /> :
                                 <Minus size={8} className="text-gray-400" />}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-[10px]">{tooltip}</TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
              {pagespeed.checkedAt && (
                <p className="text-[9px] text-[var(--text-secondary)] mt-2">Checked: {formatCheckTime(pagespeed.checkedAt)}</p>
              )}
            </>
          ) : (
            <p className="text-sm text-[var(--text-secondary)] mt-1">Pending first check</p>
          )}
        </MetricCard>
      </div>

      {/* Secondary row: Security + SEO + CMS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Security Headers */}
        <Card className="hover:border-dyndev-green/10 transition-colors">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TitleWithTooltip icon={Shield} iconColor="text-sky-400" title="Security" tooltip="Scans for 6 essential HTTP security headers: HSTS, CSP, X-Frame-Options, X-Content-Type-Options, Referrer-Policy, and Permissions-Policy. Score = percentage of headers present." side="right" />
              </div>
              {security && <StatusBadge status={security.status} />}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {security ? (
              <>
                {security.details?.error ? (
                  <>
                    <p className="text-sm text-red-400 mb-1">Check failed</p>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p className="text-[10px] text-[var(--text-secondary)] truncate cursor-help">{security.details.error}</p>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[320px] text-xs leading-relaxed break-all">{security.details.error}</TooltipContent>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <p className="text-lg font-bold mb-2">{security.score}/100</p>
                    <div className="space-y-1">
                      {Object.entries(security.details?.headers || {}).map(([name, info]: [string, any]) => (
                        <div key={name} className="flex items-center gap-1.5 text-[10px]">
                          {info.present ? (
                            <CheckCircle size={10} className="text-dyndev-green flex-shrink-0" />
                          ) : (
                            <XCircle size={10} className="text-red-400 flex-shrink-0" />
                          )}
                          <span className={info.present ? 'text-[var(--text-secondary)]' : 'text-red-400'}>{name}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <p className="text-xs text-[var(--text-secondary)]">Pending</p>
            )}
            {security?.checkedAt && (
              <p className="text-[9px] text-[var(--text-secondary)] mt-2">Checked: {formatCheckTime(security.checkedAt)}</p>
            )}
          </CardContent>
        </Card>

        {/* SEO — now shows actual title/description text */}
        <Card className="hover:border-dyndev-green/10 transition-colors">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TitleWithTooltip icon={Search} iconColor="text-amber-400" title="SEO" tooltip="Basic on-page SEO audit: meta title length (10–70 chars), meta description (50–160), exactly one H1 tag, and reachable robots.txt + sitemap. Each issue deducts points." />
              </div>
              {seo && <StatusBadge status={seo.status} />}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {seo ? (
              <>
                {seo.details?.error ? (
                  <>
                    <p className="text-sm text-red-400 mb-1">Check failed</p>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p className="text-[10px] text-[var(--text-secondary)] truncate cursor-help">{seo.details.error}</p>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[320px] text-xs leading-relaxed break-all">{seo.details.error}</TooltipContent>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <p className="text-lg font-bold mb-2">{seo.score}/100</p>
                    <div className="space-y-1 text-[10px]">
                      <div className="flex items-center gap-1.5">
                        {seo.details?.metaTitle?.present ? (
                          <CheckCircle size={10} className="text-dyndev-green" />
                        ) : (
                          <XCircle size={10} className="text-red-400" />
                        )}
                        <span className="text-[var(--text-secondary)]">
                          Meta title ({seo.details?.metaTitle?.length || 0} chars)
                        </span>
                      </div>
                      {seo.details?.metaTitle?.value && (
                        <p className="text-[10px] text-[var(--text-secondary)] italic ml-5 truncate">"{seo.details.metaTitle.value}"</p>
                      )}
                      <div className="flex items-center gap-1.5">
                        {seo.details?.metaDescription?.present ? (
                          <CheckCircle size={10} className="text-dyndev-green" />
                        ) : (
                          <XCircle size={10} className="text-red-400" />
                        )}
                        <span className="text-[var(--text-secondary)]">
                          Meta description ({seo.details?.metaDescription?.length || 0} chars)
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {seo.details?.h1Count === 1 ? (
                          <CheckCircle size={10} className="text-dyndev-green" />
                        ) : (
                          <XCircle size={10} className="text-red-400" />
                        )}
                        <span className="text-[var(--text-secondary)]">H1 tags: {seo.details?.h1Count ?? 0}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {seo.details?.sitemapReachable ? (
                          <CheckCircle size={10} className="text-dyndev-green" />
                        ) : (
                          <XCircle size={10} className="text-red-400" />
                        )}
                        <span className="text-[var(--text-secondary)]">Sitemap reachable</span>
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <p className="text-xs text-[var(--text-secondary)]">Pending</p>
            )}
            {seo?.checkedAt && (
              <p className="text-[9px] text-[var(--text-secondary)] mt-2">Checked: {formatCheckTime(seo.checkedAt)}</p>
            )}
          </CardContent>
        </Card>

        {/* CMS Detection */}
        <Card className="hover:border-dyndev-green/10 transition-colors">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TitleWithTooltip icon={Server} iconColor="text-indigo-400" title="CMS" tooltip="Detects which content management system powers the site by scanning the HTML for platform signatures. Supports WordPress (version + update check), Shopify, Wix, Squarespace, Webflow, and Drupal. Shows 'No CMS detected' for static or custom-built sites." />
              </div>
              {cms && <StatusBadge status={cms.status} />}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {cms ? (
              <>
                {cms.details?.error ? (
                  <>
                    <p className="text-sm text-red-400 mb-1">Check failed</p>
                    <p className="text-[10px] text-[var(--text-secondary)] truncate" title={cms.details.error}>{cms.details.error}</p>
                  </>
                ) : cms.details?.detected && cms.details?.cms && cms.details.cms !== 'unknown' ? (
                  <>
                    <p className="text-lg font-bold capitalize mb-1">{cms.details.cms}</p>
                    <p className="text-xs text-[var(--text-secondary)]">
                      {cms.details.cms === 'wordpress'
                        ? `Version ${cms.details.version}` + (cms.details.updateAvailable ? ' (update available)' : ' (up to date)')
                        : 'Detected'}
                    </p>
                    {cms.details.cms === 'wordpress' && cms.details.updateAvailable && (
                      <Badge variant="outline" className="mt-2 text-[9px] text-yellow-400 border-yellow-500/20">
                        Update to {cms.details.latestVersion}
                      </Badge>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-lg font-bold mb-1">No CMS detected</p>
                    <p className="text-xs text-[var(--text-secondary)]">Static or custom-built site</p>
                  </>
                )}
              </>
            ) : (
              <p className="text-xs text-[var(--text-secondary)]">Pending</p>
            )}
            {cms?.checkedAt && (
              <p className="text-[9px] text-[var(--text-secondary)] mt-2">Checked: {formatCheckTime(cms.checkedAt)}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* PageSpeed details */}
      {pagespeed?.details?.mobile && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Mobile */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)] flex items-center gap-2">
                📱 Mobile Core Web Vitals
                {pagespeed.details.mobile.score !== undefined && (
                  <span className={cn('text-[10px] font-bold', pagespeed.details.mobile.score >= 80 ? 'text-dyndev-green' : pagespeed.details.mobile.score >= 50 ? 'text-yellow-400' : 'text-red-400')}>
                    {pagespeed.details.mobile.score}/100
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {WEB_VITAL_METRICS.map(({ label, desc, tooltip }) => {
                  const value = (pagespeed.details.mobile as any)?.[label.toLowerCase()];
                  return (
                  <Tooltip key={label}>
                    <TooltipTrigger asChild>
                      <div className="text-center p-2 rounded-lg bg-[var(--bg-secondary)] cursor-help hover:bg-[var(--bg-secondary)]/80 transition-colors">
                        <p className="text-lg font-bold font-mono">{value || '—'}</p>
                        <p className="text-[10px] font-semibold text-[var(--text-secondary)]">{label}</p>
                        <p className="text-[9px] text-[var(--text-secondary)]">{desc}</p>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[240px] text-xs leading-relaxed">
                      {tooltip}
                    </TooltipContent>
                  </Tooltip>
                )})}
              </div>
            </CardContent>
          </Card>

          {/* Desktop */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)] flex items-center gap-2">
                🖥️ Desktop Core Web Vitals
                {pagespeed.details.desktop.score !== undefined && (
                  <span className={cn('text-[10px] font-bold', pagespeed.details.desktop.score >= 80 ? 'text-dyndev-green' : pagespeed.details.desktop.score >= 50 ? 'text-yellow-400' : 'text-red-400')}>
                    {pagespeed.details.desktop.score}/100
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {WEB_VITAL_METRICS.map(({ label, desc, tooltip }) => {
                  const value = (pagespeed.details.desktop as any)?.[label.toLowerCase()];
                  return (
                  <Tooltip key={label}>
                    <TooltipTrigger asChild>
                      <div className="text-center p-2 rounded-lg bg-[var(--bg-secondary)] cursor-help hover:bg-[var(--bg-secondary)]/80 transition-colors">
                        <p className="text-lg font-bold font-mono">{value || '—'}</p>
                        <p className="text-[10px] font-semibold text-[var(--text-secondary)]">{label}</p>
                        <p className="text-[9px] text-[var(--text-secondary)]">{desc}</p>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[240px] text-xs leading-relaxed">
                      {tooltip}
                    </TooltipContent>
                  </Tooltip>
                )})}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Fallback for old format (metrics only) */}
      {pagespeed?.details?.metrics && !pagespeed?.details?.mobile && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Core Web Vitals (Mobile)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: 'FCP', value: pagespeed.details.metrics.fcp, desc: 'First Contentful Paint' },
                { label: 'LCP', value: pagespeed.details.metrics.lcp, desc: 'Largest Contentful Paint' },
                { label: 'TBT', value: pagespeed.details.metrics.tbt, desc: 'Total Blocking Time' },
                { label: 'CLS', value: pagespeed.details.metrics.cls, desc: 'Cumulative Layout Shift' },
              ].map((metric) => (
                <div key={metric.label} className="text-center p-2 rounded-lg bg-[var(--bg-secondary)]">
                  <p className="text-lg font-bold font-mono">{metric.value}</p>
                  <p className="text-[10px] font-semibold text-[var(--text-secondary)]">{metric.label}</p>
                  <p className="text-[9px] text-[var(--text-secondary)]">{metric.desc}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
